/**
 * Contrato de configuración por tenant + applicationId.
 *
 * Este archivo es la ÚNICA fuente de verdad de los tipos.
 * Las claves de features/labels deben ser SEMÁNTICAS (no nombres de CSS
 * ni de componentes concretos), para poder reutilizar el contrato
 * más adelante en backend / mobile.
 */

export type ApplicationId =
    | 'default'

export type TenantId =
    | 'default'

/**
 * Catálogo cerrado de features. Agregar acá cada capacidad nueva
 * antes de usarla en un template. Esto da autocompletado y evita typos.
 */
export type FeatureKey =
    | 'sideMenu.admissions'
    | 'sideMenu.home'
    | 'sideMenu.cases'
    | 'sideMenu.schedule'
    | 'sideMenu.attentions'
    | 'sideMenu.tasks'
    | 'sideMenu.providers'
    | 'sideMenu.consolidation'
    | 'sideMenu.issues'
    | 'sideMenu.dataManagement'
    | 'sideMenu.provisionsControl'
    | 'provisionsControl.indicators.secondCard'
    | 'provisionsControl.indicators.thirdCard.subCardFour'
    | 'provisionalControl.filters.state.operables'
    | 'provisionalControl.filters.state.informables'
    | 'headerChips.help'
    | 'headerChips.userMenu'
    | 'caseData.edit'
    | 'caseData.editVisibility'
    | 'caseData.diagnosesBtn'
    | 'caseData.arrangementsBtn'
    | 'caseData.footerActions'
    | 'caseData.tracking'
    | 'caseData.patientData.actions'
    | 'caseData.exportActions'
    | 'caseData.arrangementInfo'
    | 'caseData.subtitle'
    | 'caseData.FinancierForm'
    | 'caseData.CaseManagerForm'
    | 'caseData.EmailDateForm'
    | 'caseData.ActivityFromdateForm'
    | 'caseData.FinancierEmailForm'
    | 'caseData.PatientAffiliationNumberForm'
    | 'caseData.FinancierInternalCodeForm'
    | 'caseData.CompanyTypeForm'
    | 'caseData.VatCategoryForm'
    | 'caseData.TaxZoneForm'
    | 'caseData.CaseTypeForm'
    | 'caseData.CaseComplexityForm'
    | 'caseData.CaseVisibilityForm'
    | 'caseData.DiagnosisForm'
    | 'caseData.ObservationForm'
    | 'caseData.rowBtns'
    | 'provisionsDetail.addProvision'
    | 'provisionsDetail.tableActions'
    | 'provisionsDetail.addAgreement'
    | 'provisionsDetail.agreementActions'
    | 'provisionsDetail.provisionActions'
    | 'provisionScheme.billingBtn'
    | 'provisionScheme.practicesTab'
    | 'provisionScheme.suppliesTab'
    | 'provisionScheme.ordersTab'
    | 'provisionScheme.modulesTab'
    | 'provisionScheme.consolidationLink'
    | 'admissionsDashboard.complexityIndicators'
    | 'admissionsDashboard.authorizedIndicators'
    | 'admissionsDashboard.updatesIndicator'
    | 'admissionsDashboard.budgetedState'
    | 'admissions.updateCircuit'
    | 'evolutionComponent.setVisibility'
    | 'evolutionComponent.addTask'
    | 'scheduleCase.replaceAgreementBtn'
    | 'supplyList.addSupplyBtn'
    | 'supplyList.supplyActionsBtn'
    | 'supplyList.supplyActions.update'
    | 'supplyList.supplyActions.modify'
    | 'supplyList.supplyActions.loadPrice'
    | 'supplyList.supplyActions.addPrice'
    | 'supplyList.supplyActions.remove'
    | 'supplyList.supplyActions.authorize'
    | 'supplyList.supplyActions.addQuote'
    | 'supplyList.supplyActions.addBudget'
    | 'supplyList.loadPriceDialog.SelectProvider'
    | 'provisionsDetail.statusBtn'
    | 'scheduleCase.statusSelector'
    | 'orderList.orderActions'
    | 'orderList.orderActions.view'
    | 'orderList.orderActions.modify'
    | 'orderList.orderActions.revert'
    | 'orderList.orderActions.delete'
    | 'orderList.orderActions.showDeliveryNote'
    | 'orderList.orderActions.addDeliveryNote'
    | 'orderList.billingCheckbox'
    | 'attentionListCase.stateChip'
    | 'attentionListCase,deleteBatchBtn'
    | 'indicators.practicesWithoutAttentions'
    | 'indicators.AttentionsOldAndOmited'
    | 'indicators.AttentionsWithoutBilling'
    | 'indicators.BillingAttentions'
    | 'indicators.SuppliesWithoutOrder'
    | 'indicators.SuppliesNotDelivered'
    | 'indicators.SuppliesWithoutBilling'
    | 'indicators.counterSuppliesNotInformed'
    | 'evolutionsComponent.addEvolutionBtn'
    | 'evolutionComponent.setProviderVisibility'
    | 'evolutionComponent.setFinancierVisibility'
    | 'evolutionComponent.evolution.actionMenu'
    | 'evolutionComponent.exportBtn'
    | 'home.leftCardUpdates'
    | 'home.leftCard.indicators.budgeted'
    | 'dataManagement.healthInsurance';
/**
 * Catálogo cerrado de tablas controlables por tenant.
 */
export type TableKey =
    | 'provisionsDetail.header'
    | 'provisionsDetail.expanded'
    | 'provisionsDetail.collapsed'

export interface TableConfig {
    hiddenColumns?: string[];
}

export interface TenantThemeTokens {
    colorPrimary?: string;
    colorSecondary?: string;
    borderRadius?: string;
    fontFamily?: string;
}

export interface TenantThemeConfig {
    logoUrl?: string;
    tokens: TenantThemeTokens;
}

export interface TenantApiOperationConfig {
    queryParams?: Record<string, string | number | boolean>;
    headers?: Record<string, string>;
}

export interface TenantLabelsConfig {
    [key: string]: string;
}

export interface TenantConfig {
    // tenantId: TenantId;
    // applicationId: ApplicationId;
    version: number;
    defaultRoute: RouteAccessKey;
    routes?: Partial<Record<RouteAccessKey, FeatureConfig>>;
    defaultRouteFallbacks?: RouteAccessKey[];
    features: Partial<Record<FeatureKey, FeatureConfig>>;
    tables?: Partial<Record<TableKey, TableConfig>>;
    labels?: TenantLabelsConfig;
    theme?: TenantThemeConfig;
    api?: {
        operations?: Record<string, TenantApiOperationConfig>;
        defaults?: Record<string, string | number | boolean>;
    };
}

export enum FeatureConfig {
    ENABLED = 'enabled',
    DISABLED = 'disabled',
    HIDDEN = 'hidden'
}

/**
 * Catálogo cerrado de rutas reales de la app (extraídas de los *-routing.module.ts).
 * Las rutas con parámetros se representan con el placeholder `:param` tal como
 * están declaradas en Angular Router.
 *
 * Reglas:
 *  - Prefijo `/app` para todo lo que cuelga del BaseLayout (área autenticada).
 *  - Rutas top-level (`/login`, `/not-found`) se incluyen aparte.
 *  - Agregar acá cada ruta nueva antes de poder controlarla por tenant.
 */
export type RouteAccessKey =
    // Top-level
    | '/login'
    | '/not-found'

    // Área autenticada (BaseLayout)
    | '/app'
    | '/app/principal'
    | '/app/perfil'

    // Admisiones
    | '/app/admisiones'
    | '/app/admisiones/dashboard'
    | '/app/admisiones/crear'
    | '/app/admisiones/listado'
    | '/app/admisiones/archivadas'

    // Casos
    | '/app/casos'
    | '/app/casos/listado'
    | '/app/casos/listado/:id'
    | '/app/casos/creacion'

    // Gestión (schedule)
    | '/app/gestion'
    | '/app/gestion/agenda'
    | '/app/gestion/agenda/:providerId'
    | '/app/gestion/consolidacion'
    | '/app/gestion/control-prestacional'
    | '/app/gestion/control-prestacional/:id'

    // Atenciones
    | '/app/atenciones'
    | '/app/atenciones/listado'

    // Tareas
    | '/app/tareas'
    | '/app/tareas/listado'

    // Gestión de datos
    | '/app/gestion-de-datos'
    | '/app/gestion-de-datos/obras-sociales'
    | '/app/gestion-de-datos/clientes'
    | '/app/gestion-de-datos/prestadores'
    | '/app/gestion-de-datos/operadores'
    | '/app/gestion-de-datos/practicas'
    | '/app/gestion-de-datos/insumos'
    | '/app/gestion-de-datos/modulos'
    | '/app/gestion-de-datos/liquidaciones'
    | '/app/gestion-de-datos/tipos-de-evoluciones'
    | '/app/gestion-de-datos/convenios'
    | '/app/gestion-de-datos/convenios/:id'
    | '/app/gestion-de-datos/forms'
    | '/app/gestion-de-datos/etiquetas'
    | '/app/gestion-de-datos/especialidades'

    // Prestadores
    | '/app/prestadores'
    | '/app/prestadores/buscar-prestadores'

    // Home
    | '/app/modules/home';